<script setup lang="ts">
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useAppStore } from '@/modules/app/stores/app.store'
import { useClientStore } from '@/modules/client/stores/client.store'
import { useUserStore } from '@/modules/user/stores/user.store'
import { useFilterStore } from '@/modules/filter/stores/filter.store'
import { useSearchStore } from '../stores/search.store'
import { useMotionStore } from '@/modules/motion/stores/motion.store'
import { useSideMenuStore } from '@/modules/side-menu/stores/side-menu'
import { ViewType } from '@/modules/client/types/client.enums'
import { SCROLL_MOTION } from '@/constants'
import { ROUTE_NAMES } from '@/router/constants/route.constants'
import { useToaster } from '@/modules/toaster/composables/use-toaster'
import type { SearchHit } from '../interfaces/search'
import type { ClientView } from '@/api/models/types'
import router from '@/router'
import EventBus from '@/event-bus'
import SearchBarInput from './SearchBarInput.vue'
import SearchResultMenu from './SearchResultMenu.vue'

const route = useRoute()
const appStore = useAppStore()
const userStore = useUserStore()
const clientStore = useClientStore()
const searchStore = useSearchStore()
const motionStore = useMotionStore()
const filterStore = useFilterStore()
const sideMenuStore = useSideMenuStore()

const { searchQuery, filterText } = storeToRefs(searchStore)
const { priorityItems } = storeToRefs(motionStore)

const isSearchMenuActive = ref<boolean>(false)
const isFocus = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const searchHits = ref<SearchHit[]>([])
const selected = ref<SearchHit>()
const { warning } = useToaster()

const scrollToTop = (): void => {
  EventBus.emit(SCROLL_MOTION)
}

const getSearchResult = async (): Promise<void> => {
  selected.value = undefined
  searchHits.value = []
  if (searchQuery.value.length > 3 && searchQuery.value.length <= 1000) {
    isSearchMenuActive.value = true
    isLoading.value = true
    searchHits.value = await searchStore.getSearchResult()
    isLoading.value = false
  } else {
    warning('message.searchError')
  }
}

const clearResult = (): void => {
  searchQuery.value = ''
}

const openResult = (): void => {
  if (searchHits.value.length > 0) {
    isSearchMenuActive.value = true
  }
}

const getMotionSelectedItems = async (): Promise<void> => {
  scrollToTop()
  priorityItems.value = searchHits.value.flatMap((item) =>
    item.boxes.map((box) => {
      return {
        id: box.mBoxId,
      }
    }),
  )
  filterStore.filter.status = [1, 2, 3, 4, 5, 6, 7]
  await motionStore.getMotionSelectedItems()
}

const checkSelectedClient = async (): Promise<void> => {
  if (selected.value) {
    if (selected.value.clientKey !== userStore.activeClient?.clientKey) {
      userStore.activeClient = selected.value
      await appStore.isClientReady(route.name)
    }
    if (
      selected.value.returnClientKey &&
      selected.value.returnClientKey === userStore.activeClient?.returnClientKey
    ) {
      const view = getReturnView()
      if (view) {
        clientStore.activeViewId = view.viewId
        await appStore.isViewReady(route.name)
      }
    } else {
      clientStore.activeViewId = clientStore.views[0].viewId
      await appStore.isViewReady(route.name)
    }
  }
}

const getReturnView = (): ClientView | undefined =>
  clientStore.views.find(
    (view) => view.viewId === ViewType.AgentReturn || view.viewId === ViewType.Return,
  )

const checkCurrentRoute = async (): Promise<void> => {
  if (router.currentRoute.value.name !== ROUTE_NAMES.MOTION_VIEW) {
    await router.push({ name: ROUTE_NAMES.MOTION_VIEW })
  }
}

const showResult = async (): Promise<void> => {
  searchStore.isSearchActive = true
  isSearchMenuActive.value = false
  searchStore.relatedId = undefined
  filterText.value = searchQuery.value
  sideMenuStore.close()
  await checkCurrentRoute()
  await checkSelectedClient()
  await getMotionSelectedItems()
}

watch(
  () => searchStore.isSearchActive,
  () => {
    if (!searchStore.isSearchActive) {
      isFocus.value = false
      searchStore.isSearchActive = false
      isSearchMenuActive.value = false
      selected.value = undefined
      searchHits.value = []
    }
  },
)
</script>

<template>
  <div class="relative">
    <SearchBarInput
      v-model="searchQuery"
      v-model:active="isSearchMenuActive"
      v-model:focus="isFocus"
      :title="$t('common.search')"
      @change="getSearchResult()"
      @clear="clearResult()"
      @open="openResult()"
    />
    <SearchResultMenu
      v-model="selected"
      v-model:active="isSearchMenuActive"
      :items="searchHits"
      :loading="isLoading"
      @click="showResult()"
    />
  </div>
</template>
